import { Component, DestroyRef, OnDestroy, OnInit, inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { IDashboardStats } from '../../../types/contactModule';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { MessageService } from '../../../services/message/message.service';
import { IDashboardCampaign, IDashboardMessage } from '../../../types/EngagementModule';
import { StatusComponent } from '../../../components/ui/status/status.component';
import { SmsBubbleComponent } from '../../../components/messages/sms-bubble/sms-bubble.component';
import { SmsTextComponent } from '../../../components/messages/sms-text/sms-text.component';
import { DeliveryComponent } from '../../../components/messages/delivery/delivery.component';
import {
  DeliveryService,
  DeliverySummary,
  deliveredShare,
  formatShare,
  receiptsStillDue,
} from '../../../services/message/delivery.service';
import { Subscription } from 'rxjs';
import { LiveRefresh } from '../../../services/live/live-refresh';
import { UserProfileService } from '../../../services/user/user-profile.service';
import { SentOverviewComponent } from './partials/sent-overview/sent-overview.component';
import { OutboxLineComponent } from './partials/outbox-line/outbox-line.component';
import { DashMessage } from './dash-message';
import {
  formatCount,
  formatCountdown,
  formatWhen,
  isScheduled,
  sendMoment,
  smsParts,
  toDate,
} from '../../../../utils/sms';

/** "Thursday 16 October": the page's title is the day it shows. */
const dayTitle = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

/** Refresh while something is going out or due within two minutes… */
const LIVE_FAST_MS = 15_000;
/** …and this often otherwise. */
const LIVE_CALM_MS = 30_000;
const DUE_SOON_MS = 2 * 60_000;
/** Scheduled sends asked for at once; overdue ones come first, so leave room. */
const UPCOMING_SIZE = 50;
/** A schedule this far past due did not go out (same cut-off as the sidebar badge). */
const OVERDUE_AFTER_MS = 5 * 60_000;

/**
 * Newest first by creation. The summary endpoints return rows in no set
 * order, so the "recent" lists and "Last sent" read from a sorted copy.
 */
function newestFirst<T extends { createdAt?: string | null }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0)
  );
}

/**
 * The organisation's outbox around now. A live line across the top shows the
 * last day's sends and the next day's schedule; under it the page splits at
 * now: what went out on the left, what goes out next on the right.
 */
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    RouterLink,
    StatusComponent,
    SmsBubbleComponent,
    SmsTextComponent,
    DeliveryComponent,
    SentOverviewComponent,
    OutboxLineComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private ngZone = inject(NgZone);
  router = inject(Router);
  contactsService = inject(ContactsService);
  private messageService = inject(MessageService);
  private deliveryService = inject(DeliveryService);
  private userProfileService = inject(UserProfileService);
  private destroyRef = inject(DestroyRef);

  /** Receipts for the "Last sent" message, once they load. */
  lastDelivery: DeliverySummary | null = null;
  private lastDeliveryFor = '';
  private lastDeliverySub?: Subscription;

  dashboardStats: IDashboardStats | null = null;
  dashboardCampaigns: IDashboardCampaign[] = [];
  dashboardMessages: IDashboardMessage[] = [];
  organizationsCount = 0;
  tagsCount = 0;
  segmentsCount = 0;

  upcoming: DashMessage[] = [];
  /** Scheduled sends more than five minutes past due: they did not go out. */
  overdue: DashMessage[] = [];
  /** The queue came back full, so its length may not be the whole count. */
  private upcomingFull = false;
  upcomingLoaded = false;
  /** The queue request failed: 'Up next' says so instead of "Nothing scheduled". */
  upcomingFailed = false;
  private upcomingSub?: Subscription;

  /** The newest sent message, from its own request. */
  private lastSentMessage: DashMessage | null = null;
  private lastSentLoaded = false;
  private lastSentSub?: Subscription;
  private live?: LiveRefresh;

  /** Ticks every 30 s so countdowns and "Today" labels stay current. */
  now = new Date();
  private clock?: ReturnType<typeof setInterval>;

  readonly formatWhen = formatWhen;
  readonly formatCount = formatCount;

  get loading(): boolean {
    return this.contactsService.getDashboardLoading;
  }

  get organizationName(): string {
    const organization = this.userProfileService.userDetails?.organization;
    return (organization && Object.keys(organization)[0]) || '';
  }

  get today(): string {
    return dayTitle.format(this.now);
  }

  /**
   * True until the stats request lands. The stats stream starts with zeros,
   * and "Nothing scheduled" must never flash for an organisation that is busy.
   */
  get statsPending(): boolean {
    const stats = this.dashboardStats;
    const empty =
      !stats ||
      (!stats.contactsCount &&
        !stats.campaignsCount &&
        !stats.pendingMessagesCount &&
        !stats.usersCount);
    // The headline counts the queue too, so it waits for it.
    return (this.loading && empty) || !this.upcomingLoaded;
  }

  /**
   * Sends still waiting to go out. Past-due schedules are left out: they are
   * flagged under Up next instead of being counted as scheduled.
   */
  get scheduledCount(): number {
    if (this.upcomingLoaded && !this.upcomingFailed && !this.upcomingFull) return this.upcoming.length;
    return Math.max(0, (this.dashboardStats?.pendingMessagesCount ?? 0) - this.overdue.length);
  }

  get runningCount(): number {
    return this.dashboardStats?.campaignsCount ?? 0;
  }

  /**
   * The newest send that has gone out (or is going out right now). Its own
   * request decides; the recent list is only a stand-in until it answers.
   */
  get lastSent(): DashMessage | null {
    return (
      this.lastSentMessage ??
      this.dashboardMessages.find((message) => /^(completed|active)$/i.test(message?.status ?? '')) ??
      null
    );
  }

  /** "Last sent" has nothing to show yet because its request is still out. */
  get lastSentPending(): boolean {
    return !this.lastSentLoaded && !this.lastSent;
  }

  /** "delivered so far" while receipts can still arrive; "confirmed" once they no longer will. */
  get lastShareLabel(): string {
    const delivery = this.lastDelivery;
    if (!delivery || delivery.inFlight === 0) return 'delivered';
    return receiptsStillDue(this.lastSent?.status, sendMoment(this.lastSent)) ? 'delivered so far' : 'confirmed';
  }

  /** Scheduled sends, soonest first. */
  get queue(): DashMessage[] {
    return [...this.upcoming]
      .map((message) => ({ message, at: sendMoment(message)?.getTime() ?? Infinity }))
      .sort((a, b) => a.at - b.at)
      .map(({ message }) => message);
  }

  get nextUp(): DashMessage | null {
    return this.queue[0] ?? null;
  }

  get laterInQueue(): DashMessage[] {
    return this.queue.slice(1, 4);
  }

  copyOf(message: DashMessage | null): string {
    return message?.content || message?.messageTemplate || '';
  }

  whenOf(message: DashMessage | null): string {
    return formatWhen(sendMoment(message), this.now);
  }

  countdownOf(message: DashMessage | null): string {
    return formatCountdown(sendMoment(message), this.now);
  }

  partsOf(message: DashMessage | null): number {
    return smsParts(this.copyOf(message)).parts;
  }

  isCompleted(message: DashMessage | null): boolean {
    return /^completed$/i.test(message?.status ?? '');
  }

  /** Sent messages have delivery receipts; others only have a status. */
  isSent(message: DashMessage | null): boolean {
    return /^(completed|active)$/i.test(message?.status ?? '');
  }

  get lastShare(): string {
    return this.lastDelivery ? formatShare(deliveredShare(this.lastDelivery)) : '';
  }

  /** Load receipts for whichever message is currently "Last sent". */
  private watchLastDelivery() {
    const uuid = this.lastSent?.uuid ?? '';
    if (uuid === this.lastDeliveryFor) return;

    this.lastDeliveryFor = uuid;
    this.lastDelivery = null;
    this.lastDeliverySub?.unsubscribe();
    if (!uuid) return;

    // Live while receipts are still arriving, like the delivery column.
    const message = this.lastSent;
    this.lastDeliverySub = this.deliveryService
      .live(uuid, (summary) => summary.inFlight > 0 && receiptsStillDue(message?.status, sendMoment(message)))
      .subscribe({
        next: (summary) => (this.lastDelivery = summary.total ? summary : null),
        error: () => (this.lastDelivery = null),
      });
  }

  campaignWhen(campaign: IDashboardCampaign): string {
    const date =
      toDate(campaign.startedAt) ??
      toDate(campaign.scheduledAt) ??
      toDate(campaign.createdAt);
    return formatWhen(date, this.now);
  }

  plural(count: number, one: string, many: string): string {
    return count === 1 ? one : many;
  }

  // ── navigation ──
  openMessage(message: DashMessage) {
    this.messageService.updateSelectedMessage(message as unknown as Record<string, unknown>);
    this.router.navigate(['/organization/messages', message.uuid]);
  }

  /**
   * A recent message's subject is the row's link: it navigates itself (and
   * opens a new tab on Cmd/Ctrl-click), so the row's click stays out of it.
   */
  selectFromLink(event: Event, message: DashMessage) {
    event.stopPropagation();
    this.messageService.updateSelectedMessage(message as unknown as Record<string, unknown>);
  }

  openReport(message: DashMessage) {
    this.messageService.updateSelectedMessage(message as unknown as Record<string, unknown>);
    this.router.navigate(['/organization/messages/report', message.uuid]);
  }

  openCampaign(campaign: IDashboardCampaign) {
    this.router.navigate(['/organization/campaigns', campaign.id, campaign.uuid]);
  }

  goToCreateCampaigns() {
    this.router.navigate(['/organization/campaigns/create-campaign']);
  }

  goToCreateContacts() {
    this.router.navigate(['/organization/contacts/multiple-contacts']);
  }

  goToCreateMessage() {
    this.router.navigate(['/organization/messages/create']);
  }

  ngOnInit() {
    this.contactsService.getDashboardStats
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => (this.dashboardStats = res));

    this.contactsService.getDashboardSummaryCampaigns
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => (this.dashboardCampaigns = newestFirst(res ?? [])));

    this.contactsService.getDashboardSummaryMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.dashboardMessages = newestFirst(res ?? []);
        this.watchLastDelivery();
        this.live?.nudge();
      });

    this.contactsService.getOrganizationsCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => (this.organizationsCount = count));
    this.contactsService.getTagsLength
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => (this.tagsCount = count));
    this.contactsService.getSegmentsCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => (this.segmentsCount = count));

    this.loadUpcoming();
    this.loadLastSent();

    // The portal loads the summaries once per session; coming back to the
    // dashboard later fetches them again instead of showing that first load.
    const revisit =
      this.contactsService.dashboardSummaryMessages.value?.length ||
      this.contactsService.dashboardSummaryCampaigns.value?.length;
    if (revisit) this.refreshSummaries();

    // Everything on the page keeps itself current: new sends, statuses, the
    // queue and the headline. Delivery figures refresh on their own.
    this.live = new LiveRefresh(this.ngZone, {
      refresh: () => {
        this.refreshSummaries();
        this.loadLastSent();
        this.loadUpcoming(true);
      },
      interval: () => (this.somethingGoingOut() ? LIVE_FAST_MS : LIVE_CALM_MS),
    });
    this.live.start();

    if (typeof window !== 'undefined') {
      // Outside the zone, so a pending timer never keeps the app from settling.
      this.clock = this.ngZone.runOutsideAngular(() =>
        setInterval(() => this.ngZone.run(() => (this.now = new Date())), 30_000)
      );
    }
  }

  /** `silent`: a background refresh, which keeps what is shown and never flashes a skeleton. */
  loadUpcoming(silent = false) {
    if (!silent) {
      this.upcomingLoaded = false;
      this.upcomingFailed = false;
    }
    this.upcomingSub?.unsubscribe();
    this.upcomingSub = this.messageService.fetchUpcomingMessages(UPCOMING_SIZE).subscribe({
      next: (messages) => {
        // The status filter is applied server-side; this guards the queue
        // against anything else slipping through. An instant send that is
        // still pending is going out now, not waiting in the queue.
        const scheduled = (messages as unknown as DashMessage[]).filter(
          (m) => /^pending$/i.test(m?.status ?? '') && isScheduled(m)
        );
        // A schedule well past its time is stuck, not "due now".
        const cutoff = Date.now() - OVERDUE_AFTER_MS;
        const late = (m: DashMessage) => {
          const at = sendMoment(m)?.getTime();
          return at !== undefined && at < cutoff;
        };
        this.upcoming = scheduled.filter((m) => !late(m));
        this.overdue = scheduled.filter(late);
        this.upcomingFull = messages.length >= UPCOMING_SIZE;
        this.upcomingLoaded = true;
        this.upcomingFailed = false;
        this.live?.nudge();
      },
      error: () => {
        if (silent) return;
        this.upcoming = [];
        this.overdue = [];
        this.upcomingFailed = true;
        this.upcomingLoaded = true;
      },
    });
  }

  private loadLastSent() {
    this.lastSentSub?.unsubscribe();
    this.lastSentSub = this.messageService.fetchLastSentMessage().subscribe({
      next: (message) => {
        this.lastSentMessage = message as unknown as DashMessage | null;
        this.lastSentLoaded = true;
        this.watchLastDelivery();
        this.live?.nudge();
      },
      // The recent list stands in for it.
      error: () => (this.lastSentLoaded = true),
    });
  }

  private refreshSummaries() {
    this.contactsService.fetchDashboardStats({ silent: true });
    this.contactsService.fetchDashboardSummaryMessages({ silent: true });
    this.contactsService.fetchDashboardSummaryCampaigns({ silent: true });
  }

  /** A send is going out now, or the next one is due within two minutes. */
  private somethingGoingOut(): boolean {
    if (/^active$/i.test(this.lastSent?.status ?? '')) return true;
    if (this.dashboardMessages.some((message) => /^active$/i.test(message?.status ?? ''))) return true;
    const next = sendMoment(this.nextUp)?.getTime();
    return next !== undefined && next <= Date.now() + DUE_SOON_MS;
  }

  ngOnDestroy() {
    if (this.clock) clearInterval(this.clock);
    this.live?.stop();
    this.upcomingSub?.unsubscribe();
    this.lastSentSub?.unsubscribe();
    this.lastDeliverySub?.unsubscribe();
  }
}
